/** The owner's reminders: a notification at a chosen time. */
const reminders = {
  en: {
    title: "Reminders",
    subtitle:
      "Be told at a time you choose — what to bring from the supplier before you go, or anything else worth remembering.",
    loadFailed: "Could not load your reminders.",
    saveFailed: "Could not save that reminder.",
    new: "New reminder",
    none: "No reminders yet. Make one for the morning you go to the supplier.",
    untitled: "Reminder",
    paused: "paused",
    everyDay: "Every day",
    withList: "Includes what to buy",
    withRefill: "Includes what to take from the warehouse to the shop",
    sendNow: "Send it now",
    pause: "Pause",
    resume: "Resume",
    delete: "Delete",
    deleteConfirm: "Delete this reminder?",
    sentNow: { one: "Sent to {count} device.", other: "Sent to {count} devices." },
    nothingToSay: "Nothing to send: nothing is running low right now.",
    noDevice:
      "No device is set up to receive it. Turn notifications on from this device (the card above), then try again.",
    device: {
      title: "Get reminders on this device",
      body: "This device shows a notification at the time of each reminder — the words you wrote and, if you ask for it, what is running low. Nothing else.",
      turnOn: "Turn on notifications",
      confirmHint: "Your browser will ask you to confirm.",
      on: "🔔 This device will show your reminders.",
      ios: "On iPhone, notifications only work once the dashboard is on the home screen. Tap Share, then Add to Home Screen, and open it from there.",
      denied:
        "This browser is blocking notifications for the dashboard. Allow them in the site settings, then try again.",
      failed: "Could not turn notifications on for this device just now.",
      unsupported: "This browser cannot show notifications. Use the dashboard on your phone's browser or install it.",
      unavailable: "Notifications are not available right now.",
    },
    form: {
      title: "Title",
      titlePlaceholder: "e.g. Going to the supplier",
      message: "What to remember (optional)",
      messagePlaceholder: "e.g. Ask about the new pods",
      includeRefill: "What to take from the warehouse to the shop",
      includeRefillHint:
        "The products low in the shop that the warehouse can refill, and how many to bring — the errand from home. If there is nothing to take, it stays silent.",
      includeList: "What to buy (the shopping list)",
      includeListHint:
        "What is low and not covered by the warehouse, and how many to buy, from the Stock screen. If nothing is low, a reminder with only the list stays silent.",
      time: "At",
      days: "On these days",
      everyDay: "Every day",
      workDays: "Weekdays",
      timezoneHint: "The time is your shop's own time.",
      save: "Save reminder",
    },
  },
  fr: {
    title: "Rappels",
    subtitle:
      "Soyez prévenu à l'heure de votre choix — ce qu'il faut rapporter du fournisseur avant d'y aller, ou tout ce qui mérite d'être retenu.",
    loadFailed: "Impossible de charger vos rappels.",
    saveFailed: "Impossible d'enregistrer ce rappel.",
    new: "Nouveau rappel",
    none: "Aucun rappel pour l'instant. Créez-en un pour le matin où vous allez chez le fournisseur.",
    untitled: "Rappel",
    paused: "en pause",
    everyDay: "Tous les jours",
    withList: "Avec ce qu'il faut acheter",
    withRefill: "Avec ce qu'il faut remonter de l'entrepôt en boutique",
    sendNow: "L'envoyer maintenant",
    pause: "Mettre en pause",
    resume: "Reprendre",
    delete: "Supprimer",
    deleteConfirm: "Supprimer ce rappel ?",
    sentNow: { one: "Envoyé à {count} appareil.", other: "Envoyé à {count} appareils." },
    nothingToSay: "Rien à envoyer : rien ne manque pour l'instant.",
    noDevice:
      "Aucun appareil n'est prêt à le recevoir. Activez les notifications sur cet appareil (la carte au-dessus), puis réessayez.",
    device: {
      title: "Recevoir les rappels sur cet appareil",
      body: "Cet appareil affiche une notification à l'heure de chaque rappel — vos mots et, si vous le demandez, ce qui manque. Rien d'autre.",
      turnOn: "Activer les notifications",
      confirmHint: "Votre navigateur vous demandera de confirmer.",
      on: "🔔 Cet appareil affichera vos rappels.",
      ios: "Sur iPhone, les notifications ne fonctionnent qu'une fois le tableau de bord ajouté à l'écran d'accueil. Touchez Partager, puis Sur l'écran d'accueil, et ouvrez-le depuis là.",
      denied:
        "Ce navigateur bloque les notifications du tableau de bord. Autorisez-les dans les réglages du site, puis réessayez.",
      failed: "Impossible d'activer les notifications sur cet appareil pour le moment.",
      unsupported: "Ce navigateur ne peut pas afficher de notifications. Utilisez le tableau de bord sur le navigateur de votre téléphone ou installez-le.",
      unavailable: "Les notifications ne sont pas disponibles pour le moment.",
    },
    form: {
      title: "Titre",
      titlePlaceholder: "p. ex. Chez le fournisseur",
      message: "À retenir (facultatif)",
      messagePlaceholder: "p. ex. Demander les nouveaux pods",
      includeRefill: "Ce qu'il faut remonter de l'entrepôt en boutique",
      includeRefillHint:
        "Les produits bas en boutique que l'entrepôt peut réapprovisionner, et combien en apporter — la course depuis chez vous. S'il n'y a rien à apporter, le rappel reste silencieux.",
      includeList: "Ce qu'il faut acheter (la liste d'achats)",
      includeListHint:
        "Ce qui manque et que l'entrepôt ne couvre pas, et combien en acheter, depuis l'écran Niveaux de stock. Si rien ne manque, un rappel qui ne porte que la liste reste silencieux.",
      time: "À",
      days: "Ces jours-là",
      everyDay: "Tous les jours",
      workDays: "Jours ouvrés",
      timezoneHint: "L'heure est celle de votre boutique.",
      save: "Enregistrer le rappel",
    },
  },
  ar: {
    title: "التذكيرات",
    subtitle:
      "يصلك تنبيه في الوقت الذي تختاره — ما تجلبه من المورّد قبل ذهابك، أو أي شيء آخر يستحق التذكر.",
    loadFailed: "تعذّر تحميل تذكيراتك.",
    saveFailed: "تعذّر حفظ هذا التذكير.",
    new: "تذكير جديد",
    none: "لا تذكيرات بعد. أنشئ تذكيراً لصباح ذهابك إلى المورّد.",
    untitled: "تذكير",
    paused: "متوقف",
    everyDay: "كل يوم",
    withList: "يتضمن ما يجب شراؤه",
    withRefill: "يتضمن ما تنقله من المخزن إلى المتجر",
    sendNow: "أرسله الآن",
    pause: "إيقاف مؤقت",
    resume: "استئناف",
    delete: "حذف",
    deleteConfirm: "حذف هذا التذكير؟",
    sentNow: {
      one: "أُرسل إلى جهاز واحد.",
      two: "أُرسل إلى جهازين.",
      few: "أُرسل إلى {count} أجهزة.",
      many: "أُرسل إلى {count} جهازاً.",
      other: "أُرسل إلى {count} جهاز.",
    },
    nothingToSay: "لا شيء لإرساله: لا شيء ينقص الآن.",
    noDevice: "لا جهاز مهيّأ لاستقباله. فعّل الإشعارات على هذا الجهاز (البطاقة أعلاه) ثم أعد المحاولة.",
    device: {
      title: "استقبل التذكيرات على هذا الجهاز",
      body: "يعرض هذا الجهاز إشعاراً في وقت كل تذكير — ما كتبته، وإن طلبت ما ينقص. لا شيء غير ذلك.",
      turnOn: "تفعيل الإشعارات",
      confirmHint: "سيطلب منك المتصفح التأكيد.",
      on: "🔔 سيعرض هذا الجهاز تذكيراتك.",
      ios: "على آيفون لا تعمل الإشعارات إلا بعد إضافة لوحة التحكم إلى الشاشة الرئيسية. اضغط مشاركة ثم إضافة إلى الشاشة الرئيسية وافتحها من هناك.",
      denied: "هذا المتصفح يحجب إشعارات لوحة التحكم. اسمح بها من إعدادات الموقع ثم أعد المحاولة.",
      failed: "تعذّر تفعيل الإشعارات على هذا الجهاز الآن.",
      unsupported: "لا يستطيع هذا المتصفح عرض الإشعارات. استعمل لوحة التحكم من متصفح هاتفك أو ثبّتها.",
      unavailable: "الإشعارات غير متاحة الآن.",
    },
    form: {
      title: "العنوان",
      titlePlaceholder: "مثلاً: عند المورّد",
      message: "ما تريد تذكره (اختياري)",
      messagePlaceholder: "مثلاً: اسأل عن البودات الجديدة",
      includeRefill: "ما تنقله من المخزن إلى المتجر",
      includeRefillHint:
        "المنتجات المنخفضة في المتجر التي يستطيع المخزن تعويضها وكم تحمل منها — مشوار البيت. وإن لم يكن هناك ما تحمله يبقى صامتاً.",
      includeList: "ما يجب شراؤه (قائمة الشراء)",
      includeListHint:
        "ما ينقص ولا يغطيه المخزن وكم تشتري منه، من شاشة مستويات المخزون. وإن لم ينقص شيء فالتذكير الذي لا يحمل غير القائمة يبقى صامتاً.",
      time: "في",
      days: "في هذه الأيام",
      everyDay: "كل يوم",
      workDays: "أيام العمل",
      timezoneHint: "الوقت هو توقيت متجرك.",
      save: "حفظ التذكير",
    },
  },
};

export default reminders;
